import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

export interface ContainerSize {
  width: number;
  height: number;
  /** True when container width < 500px (CRM sidebar, small iframes) */
  isCompact: boolean;
  /** True when container width <= 400px (very narrow CRM widgets) */
  isMini: boolean;
  /** True when container width <= 320px (micro CRM sidebars, e.g. Zoho Desk) */
  isMicro: boolean;
  /** True when container height < 500px (short CRM widgets) */
  isShortHeight: boolean;
}

const DEFAULT: ContainerSize = { width: 0, height: 0, isCompact: false, isMini: false, isMicro: false, isShortHeight: false };

export const ContainerSizeContext = createContext<ContainerSize>(DEFAULT);

export function useContainerSize() {
  return useContext(ContainerSizeContext);
}

/**
 * Observes element size and returns responsive flags.
 * Used by ContainerSizeProvider to measure the app root.
 */
export function useContainerSizeObserver(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState<ContainerSize>(DEFAULT);
  const prevRef = useRef({ width: 0, height: 0 });

  const update = useCallback((width: number, height: number) => {
    // Avoid re-renders when size hasn't meaningfully changed (±2px tolerance)
    const prev = prevRef.current;
    if (Math.abs(prev.width - width) < 2 && Math.abs(prev.height - height) < 2) return;
    prevRef.current = { width, height };
    setSize({ width, height, isCompact: width < 500, isMini: width <= 400, isMicro: width <= 320, isShortHeight: height < 500 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Initial measurement
    const rect = el.getBoundingClientRect();
    update(rect.width, rect.height);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        update(width, height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, update]);

  return size;
}
